import React, { useEffect, useState } from 'react';
import { logger } from '../../utils/logger';
import { PlusIcon, EditIcon, TrashIcon, UserIcon, ArchiveIcon, BanIcon, CheckCircleIcon, SearchIcon, FilterIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Modal } from '../ui/Modal';
import { Table } from '../ui/Table';
import { Badge } from '../ui/Badge';
import { User, UserRole } from '../../types';
import { supabase } from '../../utils/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useData } from '../../contexts/DataContext';
import { getRoleName, generateId } from '../../utils/helpers';
import { useConfirm } from '../../contexts/ConfirmContext';
type UserStatus = 'active' | 'inactive' | 'archived';
interface UserWithStatus extends User {
  status: UserStatus;
  gender?: string;
}
export function UserManagement() {
  const {
    showToast
  } = useToast();
  const {
    confirm
  } = useConfirm();
  const {
    isConnectedToSupabase
  } = useAuth();
  const {
    roles
  } = useData();
  const [users, setUsers] = useState<UserWithStatus[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserWithStatus[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserWithStatus | null>(null);
  // Filter states
  const [statusFilter, setStatusFilter] = useState<UserStatus | 'all'>('active');
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'administrador' as UserRole,
    gender: '' as string
  });
  const [saveMessage, setSaveMessage] = useState('');
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  // Build role options
  const roleOptions = [{
    value: 'administrador',
    label: 'Administrador'
  }, ...roles.map(r => ({
    value: r.name,
    label: r.displayName || r.name
  }))];
  // Load user statuses from localStorage
  const getUserStatuses = (): Record<string, UserStatus> => {
    try {
      const saved = localStorage.getItem('savidna-user-statuses');
      return saved ? JSON.parse(saved) : {};
    } catch (e) {
      return {};
    }
  };
  const saveUserStatus = (userId: string, status: UserStatus) => {
    const statuses = getUserStatuses();
    statuses[userId] = status;
    localStorage.setItem('savidna-user-statuses', JSON.stringify(statuses));
  };
  // Load users from Supabase
  const loadUsers = async () => {
    setIsLoadingUsers(true);
    const statuses = getUserStatuses();
    if (isConnectedToSupabase) {
      try {
        const {
          data,
          error
        } = await supabase.from('users').select('*').order('created_at', {
          ascending: false
        });
        if (data && !error) {
          const mappedUsers: UserWithStatus[] = data.map(u => ({
            id: u.id,
            email: u.email,
            password: u.password || '',
            name: u.name,
            role: u.role as UserRole,
            gender: u.gender || '',
            avatar: u.avatar,
            language: u.language || 'es',
            timezone: u.timezone || 'America/Santo_Domingo',
            createdAt: new Date(u.created_at),
            // FIXED: Prioritize Supabase status over localStorage
            status: u.status as UserStatus || statuses[u.id] || 'active'
          }));
          setUsers(mappedUsers);
          // Sync localStorage with Supabase status
          const updatedStatuses = {
            ...statuses
          };
          mappedUsers.forEach(user => {
            if (user.status !== 'active') {
              updatedStatuses[user.id] = user.status;
            }
          });
          localStorage.setItem('savidna-user-statuses', JSON.stringify(updatedStatuses));
        }
      } catch (e) {
        logger.error('Error cargando usuarios:', e);
      }
    } else {
      // Fallback for local development if needed, though usually empty initially
      // For now we rely on the auth context user or empty array
      setUsers([]);
    }
    setIsLoadingUsers(false);
  };
  useEffect(() => {
    loadUsers();
  }, [isConnectedToSupabase]);
  // Filter logic
  useEffect(() => {
    let result = users;
    // Status filter
    if (statusFilter !== 'all') {
      result = result.filter(u => u.status === statusFilter);
    }
    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(u => u.name.toLowerCase().includes(term) || u.email.toLowerCase().includes(term) || getRoleName(u.role).toLowerCase().includes(term));
    }
    setFilteredUsers(result);
  }, [users, statusFilter, searchTerm]);
  const handleOpenModal = (user?: UserWithStatus) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        name: user.name,
        email: user.email,
        password: '',
        role: user.role,
        gender: user.gender || ''
      });
    } else {
      setEditingUser(null);
      setFormData({
        name: '',
        email: '',
        password: '',
        role: 'administrador',
        gender: ''
      });
    }
    setIsModalOpen(true);
  };
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'administrador',
      gender: ''
    });
  };
  const handleSaveUser = async () => {
    if (!formData.name || !formData.email) {
      showToast({
        type: 'warning',
        title: 'Campos requeridos',
        message: 'Por favor completa todos los campos requeridos.'
      });
      return;
    }
    if (!editingUser && !formData.password) {
      showToast({
        type: 'warning',
        title: 'Contraseña requerida',
        message: 'La contraseña es requerida para nuevos usuarios.'
      });
      return;
    }
    confirm({
      title: editingUser ? 'Guardar Cambios' : 'Crear Usuario',
      message: editingUser ? '¿Estás seguro de que deseas guardar los cambios de este usuario?' : '¿Estás seguro de que deseas registrar a este nuevo usuario?',
      confirmText: 'Guardar',
      onConfirm: async () => {
        setIsSaving(true);
        if (isConnectedToSupabase) {
          try {
            if (editingUser) {
              // Update in Supabase
              const updateData: any = {
                name: formData.name,
                email: formData.email.toLowerCase().trim(),
                role: formData.role,
                gender: formData.gender || null
              };
              if (formData.password) {
                updateData.password = formData.password;
              }
              const {
                error
              } = await supabase.from('users').update(updateData).eq('id', editingUser.id);
              if (error) {
                setIsSaving(false);
                showToast({
                  type: 'error',
                  title: 'Error al actualizar',
                  message: 'Error actualizando usuario: ' + error.message
                });
                return;
              }
              // Update local state immediately (optimistic)
              setUsers(prev => prev.map(u => u.id === editingUser.id ? {
                ...u,
                name: formData.name,
                email: formData.email.toLowerCase().trim(),
                role: formData.role as UserRole,
                gender: formData.gender || ''
              } : u));
              setSaveMessage('✅ Usuario actualizado');
              showToast({
                type: 'success',
                title: 'Usuario actualizado',
                message: `${formData.name} fue actualizado correctamente.`
              });
            } else {
              // Create in Supabase
              const newId = crypto.randomUUID();
              const {
                error
              } = await supabase.from('users').insert({
                id: newId,
                email: formData.email.toLowerCase().trim(),
                password: formData.password,
                name: formData.name,
                role: formData.role,
                gender: formData.gender || null,
                language: 'es',
                timezone: 'America/Santo_Domingo'
              });
              if (error) {
                setIsSaving(false);
                showToast({
                  type: 'error',
                  title: 'Error al crear',
                  message: 'Error creando usuario: ' + error.message
                });
                return;
              }
              // Add to local state immediately
              const statuses = getUserStatuses();
              setUsers(prev => [{
                id: newId,
                name: formData.name,
                email: formData.email.toLowerCase().trim(),
                password: '',
                role: formData.role as UserRole,
                gender: formData.gender || '',
                language: 'es',
                timezone: 'America/Santo_Domingo',
                createdAt: new Date(),
                status: (statuses[newId] || 'active') as UserStatus
              }, ...prev]);
              setSaveMessage('✅ Usuario creado');
              showToast({
                type: 'success',
                title: 'Usuario creado',
                message: `${formData.name} fue creado correctamente.`
              });
            }
          } catch (e: any) {
            setIsSaving(false);
            showToast({
              type: 'error',
              title: 'Error',
              message: e.message || 'Error desconocido'
            });
            return;
          }
        } else {
          setIsSaving(false);
          showToast({
            type: 'warning',
            title: 'Sin conexión',
            message: 'Se requiere conexión a Supabase para gestionar usuarios.'
          });
          return;
        }
        setIsSaving(false);
        handleCloseModal();
        setTimeout(() => setSaveMessage(''), 3000);
      }
    });
  };
  const handleStatusChange = (userId: string, newStatus: UserStatus) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;
    let title = '';
    let message = '';
    let confirmText = '';
    let variant: 'primary' | 'danger' | 'warning' | 'info' = 'primary';
    if (newStatus === 'archived') {
      title = 'Archivar Usuario';
      message = `¿Estás seguro de que deseas archivar a "${user.name}"? Podrás restaurarlo más tarde.`;
      confirmText = 'Archivar';
      variant = 'warning';
    } else if (newStatus === 'inactive') {
      title = 'Desactivar Usuario';
      message = `¿Estás seguro de que deseas desactivar a "${user.name}"? No podrá acceder al sistema.`;
      confirmText = 'Desactivar';
      variant = 'warning';
    } else if (newStatus === 'active') {
      title = 'Activar Usuario';
      message = `¿Estás seguro de que deseas activar a "${user.name}"?`;
      confirmText = 'Activar';
      variant = 'primary';
    }
    confirm({
      title,
      message,
      confirmText,
      variant,
      onConfirm: () => {
        saveUserStatus(userId, newStatus);
        setUsers(prev => prev.map(u => u.id === userId ? {
          ...u,
          status: newStatus
        } : u));
        // Persist to Supabase
        supabase.from('users').update({
          status: newStatus
        }).eq('id', userId).then(({
          error
        }) => {
          if (error) logger.error('Error updating user status in Supabase:', error);else logger.log('✅ User status updated in Supabase:', newStatus);
        });
        const actionName = newStatus === 'active' ? 'activado' : newStatus === 'inactive' ? 'desactivado' : 'archivado';
        const toastType = newStatus === 'active' ? 'success' : newStatus === 'inactive' ? 'error' : 'warning';
        showToast({
          type: toastType,
          title: `Usuario ${actionName}`,
          message: `El usuario fue ${actionName} correctamente.`
        });
        setSaveMessage(`Usuario ${actionName} correctamente`);
        setTimeout(() => setSaveMessage(''), 3000);
      }
    });
  };
  const getRoleBadgeVariant = (role: UserRole) => {
    if (role === 'administrador') return 'primary';
    return 'info';
  };
  const getStatusBadgeVariant = (status: UserStatus) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'warning';
      case 'archived':
        return 'default';
    }
  };
  const getStatusLabel = (status: UserStatus) => {
    switch (status) {
      case 'active':
        return 'Activo';
      case 'inactive':
        return 'Inactivo';
      case 'archived':
        return 'Archivado';
    }
  };
  const columns = [{
    key: 'name',
    header: 'Usuario',
    render: (user: UserWithStatus) => <div className="flex items-center gap-3" data-id="element-6154" data-mp="3k5944la7t80e">
          {user.avatar ? <img src={user.avatar} alt={user.name} className={`w-10 h-10 rounded-full object-cover border-2 ${user.status === 'active' ? 'border-green-400' : 'border-gray-200 dark:border-gray-600'}`} data-id="element-6155" data-mp="3f7pfqsjszeuq" /> : <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${user.status === 'active' ? 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400' : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'}`} data-id="element-6156" data-mp="1migae6zi5hr4">
              {user.name.charAt(0).toUpperCase()}
            </div>}
          <div data-id="element-6157" data-mp="2195trwn4a362">
            <div className={`font-medium ${user.status === 'archived' ? 'text-gray-500 line-through' : 'text-gray-900 dark:text-white'}`} data-id="element-6158" data-mp="13jc8rjaaiq9h">
              {user.name}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-6159" data-mp="1dhs1fqoyu7l3">
              {user.email}
            </div>
          </div>
        </div>
  }, {
    key: 'role',
    header: 'Rol',
    render: (user: UserWithStatus) => <Badge variant={getRoleBadgeVariant(user.role)} data-id="element-6160" data-mp="2ow5gc61qrcoz">
          {getRoleName(user.role)}
        </Badge>
  }, {
    key: 'status',
    header: 'Estado',
    render: (user: UserWithStatus) => <Badge variant={getStatusBadgeVariant(user.status)} data-id="element-6161" data-mp="iahjioja6sgm">
          {getStatusLabel(user.status)}
        </Badge>
  }, {
    key: 'actions',
    header: 'Acciones',
    render: (user: UserWithStatus) => <div className="flex items-center gap-1" data-id="element-6162" data-mp="3js5leefqz92">
          <button onClick={() => handleOpenModal(user)} className="p-1.5 text-gray-500 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded-lg transition-colors" title="Editar usuario" data-id="element-6163" data-mp="3iz7f1tjli2j">
            <EditIcon className="w-4 h-4" data-id="element-6164" data-mp="3loloznqe3q9l" />
          </button>

          {user.status === 'active' ? <button onClick={() => handleStatusChange(user.id, 'inactive')} className="p-1.5 text-gray-500 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors" title="Desactivar usuario" data-id="element-6165" data-mp="3gqk4cydbfiw0">
              <BanIcon className="w-4 h-4" data-id="element-6166" data-mp="39f5s5xz3yd3v" />
            </button> : <button onClick={() => handleStatusChange(user.id, 'active')} className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors" title="Activar usuario" data-id="element-6167" data-mp="39d1ewpfbk1qx">
              <CheckCircleIcon className="w-4 h-4" data-id="element-6168" data-mp="34g2qmnic6p0n" />
            </button>}

          {user.status !== 'archived' ? <button onClick={() => handleStatusChange(user.id, 'archived')} className="p-1.5 text-gray-500 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors" title="Archivar usuario" data-id="element-6169" data-mp="z2o0277f6ka">
              <ArchiveIcon className="w-4 h-4" data-id="element-6170" data-mp="3n83r1uarenc6" />
            </button> : <button onClick={() => handleStatusChange(user.id, 'active')} className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors" title="Restaurar usuario" data-id="element-6171" data-mp="26u5cf2azp5ui">
              <PlusIcon className="w-4 h-4" data-id="element-6172" data-mp="otwb0vpr3ycd" />
            </button>}
        </div>
  }];
  return <div className="space-y-6" data-id="element-6173" data-mp="12s2v991s1ztk">
      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-6174" data-mp="ytwuqirj8gh3">
        <div data-id="element-6175" data-mp="b42j24iy8xyf">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-6176" data-mp="b3ssaeq2ymro">
            <UserIcon className="w-6 h-6 text-teal-600 dark:text-teal-400" data-id="element-6177" data-mp="657ne1zvveww" />
            Gestión de Usuarios
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-6178" data-mp="jxfjrsn993v2">
            {users.length} usuarios registrados en el sistema
          </p>
        </div>
        <Button onClick={() => handleOpenModal()} data-id="element-6179" data-mp="3njcuiiv2zo6e">
          <PlusIcon className="w-4 h-4" data-id="element-6180" data-mp="3ni1ril9gmkgc" />
          Nuevo Usuario
        </Button>
      </div>

      {saveMessage && <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-600 dark:text-green-400 text-sm animate-in fade-in slide-in-from-top-2" data-id="element-6181" data-mp="c8harmx9tvqk">
          {saveMessage}
        </div>}

      {/* Filters */}
      <Card className="p-4" data-id="element-6182" data-mp="lb08jsm79i5y">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between" data-id="element-6183" data-mp="q7qnudq6k9qb">
          <div className="flex bg-gray-100 dark:bg-gray-800 p-1 rounded-lg w-full md:w-auto" data-id="element-6184" data-mp="v73fdbrzy4z8">
            {(['active', 'inactive', 'archived', 'all'] as const).map(status => <button key={status} onClick={() => setStatusFilter(status)} className={`
                  px-4 py-1.5 text-sm font-medium rounded-md transition-all flex-1 md:flex-none capitalize
                  ${statusFilter === status ? 'bg-white dark:bg-gray-700 text-teal-600 dark:text-teal-400 shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}
                `} data-id="element-6185" data-mp="14wv7lzcmim0j">
                  {status === 'all' ? 'Todos' : getStatusLabel(status)}
                </button>)}
          </div>

          <div className="relative w-full md:w-64" data-id="element-6186" data-mp="1j1n9o9y46bhx">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-6187" data-mp="1j48cp2a5exh9" />
            <input type="text" placeholder="Buscar usuarios..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-9 pr-4 py-2 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all" data-id="element-6188" data-mp="2bqt422qmj9kt" />
          </div>
        </div>
      </Card>

      {/* Users List */}
      <Card className="overflow-hidden" data-id="element-6189" data-mp="2pqvej3wlr0q1">
        {isLoadingUsers ? <div className="p-12 text-center text-gray-500" data-id="element-6190" data-mp="2por2fyojuveo">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500 mx-auto mb-3" data-id="element-6191" data-mp="k7cxrgagd0x8"></div>
            Cargando usuarios...
          </div> : filteredUsers.length === 0 ? <div className="py-12 text-center text-gray-500 dark:text-gray-400 text-sm" data-id="element-6192" data-mp="b6oiki9bouir">
            No se encontraron usuarios con los filtros seleccionados
          </div> : <>
            {/* Mobile cards */}
            <div className="sm:hidden divide-y divide-gray-100 dark:divide-gray-800" data-id="element-6193" data-mp="13thrvp0s324d">
              {filteredUsers.map(user => <div key={user.id} className="px-4 py-3 flex items-center gap-3" data-id="element-6194" data-mp="u47xu3mdj9dm">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold flex-shrink-0 ${user.status === 'active' ? 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400' : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'}`} data-id="element-6195" data-mp="p4linomzjj26">
                    {user.avatar ? <img src={user.avatar} alt={user.name} className="w-10 h-10 rounded-full object-cover" data-id="element-6196" data-mp="1u41jzc4uw7a8" /> : user.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-6197" data-mp="28317uhkjoi5c">
                    <p className={`font-semibold text-sm truncate ${user.status === 'archived' ? 'text-gray-400 line-through' : 'text-gray-900 dark:text-white'}`} data-id="element-6198" data-mp="2d35t0kwee11w">
                      {user.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate" data-id="element-6199" data-mp="2msfng7eprvt6">
                      {user.email}
                    </p>
                    <div className="flex items-center gap-2 mt-1" data-id="element-6200" data-mp="2vtdrgpp5k3br">
                      <Badge variant={getRoleBadgeVariant(user.role)} className="text-xs py-0 px-1.5" data-id="element-6201" data-mp="2vrkk186ptf2p">
                        {getRoleName(user.role)}
                      </Badge>
                      <Badge variant={getStatusBadgeVariant(user.status)} className="text-xs py-0 px-1.5" data-id="element-6202" data-mp="15hgwker9j1rk">
                        {getStatusLabel(user.status)}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0" data-id="element-6203" data-mp="1af8sdjv13811">
                    <button onClick={() => handleOpenModal(user)} className="p-2 text-gray-400 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded-lg transition-colors" title="Editar" data-id="element-6204" data-mp="1ogl6dpqmozka">
                      <EditIcon className="w-4 h-4" data-id="element-6205" data-mp="3m64bs9u509hr" />
                    </button>
                    {user.status === 'active' ? <button onClick={() => handleStatusChange(user.id, 'inactive')} className="p-2 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors" title="Desactivar" data-id="element-6206" data-mp="3c37cz9a517gs">
                        <BanIcon className="w-4 h-4" data-id="element-6207" data-mp="379wf3xnb0110" />
                      </button> : <button onClick={() => handleStatusChange(user.id, 'active')} className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors" title="Activar" data-id="element-6208" data-mp="2scdik94dwjj0">
                        <CheckCircleIcon className="w-4 h-4" data-id="element-6209" data-mp="2ocv8y9i0juvh" />
                      </button>}
                  </div>
                </div>)}
            </div>
            {/* Desktop table */}
            <div className="hidden sm:block" data-id="element-6210" data-mp="mivwhkt5n7h7">
              <Table columns={columns} data={filteredUsers} keyExtractor={user => user.id} emptyMessage="No se encontraron usuarios" data-id="element-6211" data-mp="2iod5vdm0vy4a" />
            </div>
          </>}
      </Card>

      {/* User Modal */}
      <Modal isOpen={isModalOpen} onClose={handleCloseModal} title={editingUser ? 'Editar Usuario' : 'Nuevo Usuario'} size="md" icon={<UserIcon className="w-5 h-5" data-id="element-6213" data-mp="1zr27dv87t8pw" />} accentColor="sky" data-id="element-6212" data-mp="24ovb8hm1pqn0">
        <div className="space-y-6" data-id="element-6214" data-mp="1ux7p8w89d1sx">
          {/* Personal Info Section */}
          <div data-id="element-6215" data-mp="1q0h783cw7w4v">
            <div className="flex items-center gap-2 mb-4" data-id="element-6216" data-mp="1pzz2sjg2ublv">
              <span className="w-1.5 h-5 rounded-full bg-teal-500" data-id="element-6217" data-mp="1l0uv5x8swlxn"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-6218" data-mp="1gyrix6eb5dbb">
                Información Personal
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-6219" data-mp="1c1rcuvhgkak7">
              <Input label="Nombre Completo" value={formData.name} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} placeholder="Ej: Juan Pérez" data-id="element-6220" data-mp="3slusztspezri" />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-6221" data-mp="34xtpizadu9iy">
                <Input label="Correo Electrónico" type="email" value={formData.email} onChange={e => setFormData({
                ...formData,
                email: e.target.value
              })} placeholder="usuario@ejemplo.com" data-id="element-6222" data-mp="38xc7pnatk6i3" />

                <Select label="Género" options={[{
                value: 'masculino',
                label: 'Masculino'
              }, {
                value: 'femenino',
                label: 'Femenino'
              }, {
                value: 'otro',
                label: 'Otro'
              }]} value={formData.gender} onChange={value => setFormData({
                ...formData,
                gender: value
              })} data-id="element-6223" data-mp="2q6ug5m1q3gyb" />
              </div>
            </div>
          </div>

          {/* Access & Security Section */}
          <div data-id="element-6224" data-mp="3ejpp1ec8r3ee">
            <div className="flex items-center gap-2 mb-4" data-id="element-6225" data-mp="3endjcfpiftu6">
              <span className="w-1.5 h-5 rounded-full bg-amber-500" data-id="element-6226" data-mp="yt3gsq761vvs"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-6227" data-mp="tva9tuy1gxug">
                Acceso y Seguridad
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-6228" data-mp="12ef0lfhzp6gx">
              <Input label={editingUser ? 'Nueva Contraseña (opcional)' : 'Contraseña'} type="password" value={formData.password} onChange={e => setFormData({
              ...formData,
              password: e.target.value
            })} placeholder="••••••••" data-id="element-6229" data-mp="seo5bbllunfs" />

              <Select label="Rol del Usuario" options={roleOptions} value={formData.role} onChange={value => setFormData({
              ...formData,
              role: value as UserRole
            })} data-id="element-6230" data-mp="1bamlqv5b6x6z" />
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700" data-id="element-6231" data-mp="8ryaqnbckhkt">
            <Button variant="secondary" onClick={handleCloseModal} disabled={isSaving} data-id="element-6232" data-mp="3x27er0nu7se">
              Cancelar
            </Button>
            <Button onClick={handleSaveUser} isLoading={isSaving} data-id="element-6233" data-mp="3r5pcdgs8r9q9">
              {editingUser ? 'Actualizar' : 'Crear'} Usuario
            </Button>
          </div>
        </div>
      </Modal>
    </div>;
}